import { useEffect, useRef, useCallback } from 'react';

interface UseInactivityTimeoutOptions {
  timeout: number; // in milliseconds
  onTimeout: () => void;
  enabled?: boolean;
  warningTime?: number; // time before timeout to show warning (in milliseconds)
  onWarning?: () => void;
}

/**
 * Hook to detect user inactivity and trigger a callback after a specified timeout.
 * Tracks mouse movements, keyboard input, clicks, scrolls, and touch events.
 */
export const useInactivityTimeout = ({
  timeout,
  onTimeout,
  enabled = true,
  warningTime,
  onWarning,
}: UseInactivityTimeoutOptions) => {
  const timeoutRef = useRef<NodeJS.Timeout | null>(null);
  const warningTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const lastActivityRef = useRef<number>(Date.now());
  const warningShownRef = useRef<boolean>(false);

  const clearTimeouts = useCallback(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
    if (warningTimeoutRef.current) {
      clearTimeout(warningTimeoutRef.current);
      warningTimeoutRef.current = null;
    }
  }, []);

  const resetTimer = useCallback(() => {
    if (!enabled) return;

    lastActivityRef.current = Date.now();
    warningShownRef.current = false;
    clearTimeouts();

    // Set warning timeout if configured
    if (warningTime && onWarning && timeout > warningTime) {
      warningTimeoutRef.current = setTimeout(() => {
        if (!warningShownRef.current) {
          warningShownRef.current = true;
          onWarning();
        }
      }, timeout - warningTime);
    }

    // Set main timeout
    timeoutRef.current = setTimeout(() => {
      onTimeout();
    }, timeout);
  }, [timeout, onTimeout, enabled, warningTime, onWarning, clearTimeouts]);

  useEffect(() => {
    if (!enabled) {
      clearTimeouts();
      return;
    }

    // Events that indicate user activity
    const activityEvents = [
      'mousedown',
      'mousemove',
      'keydown',
      'scroll',
      'touchstart',
      'click',
      'wheel',
    ];

    // Throttle the reset to avoid excessive calls
    let throttleTimer: NodeJS.Timeout | null = null;
    const throttledReset = () => {
      if (throttleTimer) return;
      throttleTimer = setTimeout(() => {
        throttleTimer = null;
        resetTimer();
      }, 1000); // Throttle to once per second
    };

    // Add event listeners
    activityEvents.forEach((event) => {
      document.addEventListener(event, throttledReset, { passive: true });
    });

    // Also listen for visibility changes
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        // Check if we've been inactive too long while tab was hidden
        const inactiveTime = Date.now() - lastActivityRef.current;
        if (inactiveTime >= timeout) {
          onTimeout();
        } else {
          resetTimer();
        }
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Start the timer
    resetTimer();

    // Cleanup
    return () => {
      clearTimeouts();
      if (throttleTimer) clearTimeout(throttleTimer);
      activityEvents.forEach((event) => {
        document.removeEventListener(event, throttledReset);
      });
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [enabled, resetTimer, clearTimeouts, timeout, onTimeout]);

  // Return a function to manually reset the timer
  return { resetTimer, lastActivity: lastActivityRef.current };
};

export default useInactivityTimeout;
